import { useLiveQuery } from 'dexie-react-hooks';
import { Lock, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { buyerLedger, type DomainPayment, type DomainSale } from '@dairy/domain';
import { birrInputValue, newId, PAYMENT_METHODS, tryParseBirr, type PaymentMethod } from '@dairy/shared';
import { useIsOwner, useProfile, useSettings } from '@/auth/useSession';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DateField } from '@/components/DateField';
import { MoneyInput } from '@/components/inputs';
import { Button, Card, Dialog, Field, Input, PageHeader, Select } from '@/components/ui';
import { errorMessage } from '@/components/errors';
import { db } from '@/db/db';
import { isLocked, remove, save } from '@/db/repo';
import { rememberLastUsed, useLastUsed, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';

/** Screen 17 — Record payment (IN-3): pays off the oldest unpaid sales first; shows the new balance and what it paid off. */
export function PaymentFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { t } = useTranslation();
  const f = useFormat();
  const s = useSettings();
  const profile = useProfile();
  const isOwner = useIsOwner();
  const today = useToday();
  const navigate = useNavigate();
  const last = useLastUsed();
  const existing = useLiveQuery(() => (id ? db.payments.get(id) : undefined), [id]);
  const buyers = useLiveQuery(() => db.buyers.toArray(), []) ?? [];
  const [buyerId, setBuyerId] = useState(params.get('buyerId') ?? '');
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>((last.paymentMethod as PaymentMethod) ?? 'CASH');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ balance: number; paidOff: Array<{ date: string; amount: number }> } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setBuyerId(existing.buyerId);
    setDate(existing.date);
    setAmount(birrInputValue(existing.amountSantim));
    setMethod(existing.method);
    setReference(existing.reference ?? '');
  }, [existing]);

  const locked = !!existing && !isOwner && (existing.createdBy !== profile.userId || isLocked(existing, profile, s));

  const submit = async () => {
    const a = tryParseBirr(amount);
    if (!buyerId) return setError(t('money.errors.buyer'));
    if (!a.ok || !a.santim) return setError(t('money.amountRequired'));
    const sales = (await db.sales.where('buyerId').equals(buyerId).toArray()) as unknown as DomainSale[];
    const before = buyerLedger(sales, (await db.payments.where('buyerId').equals(buyerId).toArray()) as unknown as DomainPayment[], today, s.receivableOverdueDays);
    try {
      await save('payment', { ...(existing ?? {}), id: existing?.id ?? newId(), buyerId, date, amountSantim: a.santim, method, reference: reference.trim() || null, saleId: existing?.saleId ?? null });
      await rememberLastUsed('paymentMethod', method);
      const after = buyerLedger(sales, (await db.payments.where('buyerId').equals(buyerId).toArray()) as unknown as DomainPayment[], today, s.receivableOverdueDays);
      const stillUnpaid = new Set(after.unpaid.map((u) => u.saleId));
      setResult({
        balance: after.balanceSantim,
        paidOff: before.unpaid.filter((u) => !stillUnpaid.has(u.saleId)).map((u) => ({ date: u.date, amount: u.unpaidSantim })),
      });
    } catch (e) {
      setError(errorMessage(t, e));
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <PageHeader title={existing ? t('money.editPayment') : t('money.recordPayment')} />
      {locked ? (
        <p className="flex items-center gap-2 rounded-xl bg-stone-200 p-3">
          <Lock className="h-4 w-4" /> {t('common.lockedAskOwner')}
        </p>
      ) : null}
      <fieldset disabled={locked}>
        <Card className="space-y-4">
          <Field label={t('money.buyer')}>
            <Select value={buyerId} onChange={(e) => setBuyerId(e.target.value)} disabled={!!existing}>
              <option value="">{t('money.chooseBuyer')}</option>
              {buyers.filter((b) => b.active || b.id === buyerId).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.date')}>
            <DateField value={date} max={today} onChange={setDate} />
          </Field>
          <Field label={t('money.amount')}>
            <MoneyInput value={amount} onChange={(e) => setAmount(e.target.value)} />
          </Field>
          <Field label={t('money.method')}>
            <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {t(`money.methodType.${m}`)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('money.reference')} hint={t('money.referenceHint')}>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} maxLength={100} />
          </Field>
          {error ? <p className="text-red-700">{error}</p> : null}
          <Button size="lg" className="w-full" onClick={() => void submit()}>
            {t('common.save')}
          </Button>
          {existing ? (
            <Button variant="ghost" className="w-full" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-5 w-5" /> {t('common.delete')}
            </Button>
          ) : null}
        </Card>
      </fieldset>
      <Dialog
        open={!!result}
        onOpenChange={(o) => !o && navigate(`/buyers/${buyerId}`, { replace: true })}
        title={t('money.paymentSaved')}
        footer={<Button onClick={() => navigate(`/buyers/${buyerId}`, { replace: true })}>{t('common.done')}</Button>}
      >
        {result ? (
          <div className="space-y-3">
            <p className="text-lg">
              {t('money.newBalance')}: <b>{result.balance < 0 ? `${t('money.credit')} ${f.birr(-result.balance)}` : f.birr(result.balance)}</b>
            </p>
            {result.paidOff.length ? (
              <div>
                <div className="font-medium">{t('money.paidOff')}</div>
                <ul className="list-disc pl-5">
                  {result.paidOff.map((p) => (
                    <li key={p.date + p.amount}>
                      {f.short(p.date)} · {f.birr(p.amount)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </Dialog>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('money.deletePayment')}
        danger
        confirmLabel={t('common.delete')}
        onConfirm={async () => {
          if (!existing) return;
          await remove('payment', existing.id);
          navigate(-1);
        }}
      />
    </div>
  );
}
